import type { HookAlertType } from '@cobliteam/api/blueprintAlerts';
import { ConditionCategory } from '@cobliteam/api-lib/src/dashboard/alerts/definitions/conditions';
import DayOfWeek from '@cobliteam/api-lib/src/dashboard/alerts/definitions/dates';
import { TriggerDriverIdentificationRecurrence } from '@cobliteam/api-lib/src/dashboard/alerts/definitions/driverIdentification';
import { type Rule, RuleCategory, RuleMode } from '@cobliteam/api-lib/src/dashboard/alerts/definitions/rules';
import type { Trigger, TriggerRecord } from '@cobliteam/api-lib/src/dashboard/alerts/definitions/triggers';
import type { TriggersFilters } from '@cobliteam/api-lib/src/dashboard/alerts/utils';
import type { UUID } from '@cobliteam/common-types';
import { type AlertHookFormValues, type AlertHookParams, AlertHookType } from './types';

/**
 * One row in the read-only summary shown on the success step of `DialogAlertHook` (AE-2326).
 * Both label and value are i18n keys; runtime values (e.g. vehicle count) are interpolated via ICU.
 */
interface AlertHookSummaryField {
  /** i18n key under the `alertHooks` namespace for the row label. */
  labelKey: string;
  /** i18n key under the `alertHooks` namespace for the row value. */
  valueKey: string;
  /** Optional ICU interpolation values for `valueKey`. */
  valueParams?: Record<string, unknown>;
}

interface AlertHookCopy {
  /** Step 1 — pitches the alert benefits. i18n keys in the `alertHooks` namespace. */
  suggestion: {
    /** Lucide icon name rendered in the colored circle at the top of step 1. */
    iconName: string;
    /** Bg color token for the icon circle (e.g. `bg-primary-3`). */
    iconBg: string;
    titleKey: string;
    descriptionKey: string;
    /** i18n key returning an array of `{ icon, label, description }` items (4 entries). */
    benefitsKey: string;
  };
  /** Step 2 — previews the pre-configured trigger before the user activates it. */
  confirm: {
    titleKey: string;
    /** Icon of step 2, when it differs from step 1's. */
    iconName?: string;
    /** Replaces `alertHooks:common.confirmDescription`. */
    descriptionKey?: string;
    /** Replaces `alertHooks:common.ctaEditBeforeActivate`. */
    editCtaKey?: string;
  };
  /** i18n key for the default rule name placed in the payload's `name` field. */
  defaultNameKey: string;
}

interface AlertHookBaseEntry {
  /** Key under which a dismissal is stored, so the hook won't be suggested again for the same context. */
  getDismissKey: (params: AlertHookParams) => string;
  /** Read-only summary rows shown on the success step. */
  buildSummaryRows: (params: AlertHookParams) => AlertHookSummaryField[];
  copy: AlertHookCopy;
}

/** Hook activated by creating a trigger in the alerts API, from a rule/condition of the catalog. */
interface TriggerAlertHookConfigEntry extends AlertHookBaseEntry {
  kind: 'trigger';
  ruleCategory: RuleCategory;
  conditionCategoryDefault: ConditionCategory;
  ruleMode: RuleMode;
  /** Query filter used by `useTriggerAlertHook` to fetch existing triggers for the dedup check. */
  buildFilter: (params: AlertHookParams) => TriggersFilters;
  /** Builds everything but `conditionId` — resolve that with `resolveConditionId` and merge in. */
  buildPayload: (params: AlertHookParams, formValues: AlertHookFormValues) => Omit<TriggerRecord, 'conditionId'>;
}

/**
 * Hook activated by the blueprint-api, which owns the template (`CreateHookAlert`). The panel only
 * shows the preview; name, schedule and recipients come from the template on the server.
 */
interface BlueprintAlertHookConfigEntry extends AlertHookBaseEntry {
  kind: 'blueprint';
  /** Kebab-case alert type: the create-page route and the analytics `alert_type`. */
  alertType: string;
  /** The template's `alert_type` in `CreateHookAlert`, and the `event.nameIdentifier` of the rules it creates. */
  eventNameIdentifier: HookAlertType;
}

type AlertHookConfigEntry = TriggerAlertHookConfigEntry | BlueprintAlertHookConfigEntry;

type BlueprintAlertHookType = AlertHookType.CHECKLIST_NON_COMPLIANCE;
type TriggerAlertHookType = Exclude<AlertHookType, BlueprintAlertHookType>;

const EVERY_DAY: DayOfWeek[] = [DayOfWeek.SUNDAY, DayOfWeek.MONDAY, DayOfWeek.TUESDAY, DayOfWeek.WEDNESDAY, DayOfWeek.THURSDAY, DayOfWeek.FRIDAY, DayOfWeek.SATURDAY];
const FULL_DAY_START = '00:00:00';
const FULL_DAY_END = '23:59:59';
const DEDUP_PAGE_SIZE = 200;

/**
 * The API requires a recurrence for DRIVER_IDENTIFICATION_WITHOUT_DRIVER triggers. The hook flow has
 * no recurrence field, so this default is used both by `buildPayload` (one-click activation) and by
 * the TriggerPage prefill when the user edits the suggested alert before activating it.
 */
const DEFAULT_IDENTIFICATION_RECURRENCE = TriggerDriverIdentificationRecurrence.EACH_1_HOUR;

// Dedup is fleet-wide by RuleCategory (decision AE-2325): suppress if the fleet already has any
// active fleet-policy trigger of the hook's category — so the filter is the same for every type and
// the category match happens in `useTriggerAlertHook`.
const fleetWideActiveFilter = (): TriggersFilters => ({ page: 0, size: DEDUP_PAGE_SIZE, active: true, ruleMode: RuleMode.FLEET_POLICY });

const dismissKeyFor = (type: AlertHookType, ids?: UUID[]): string => {
  const present = (ids ?? []).filter(Boolean);
  return present.length === 0 ? type : `${type}:${[...present].sort().join(',')}`;
};

const buildBasePayload = (
  formValues: AlertHookFormValues,
  scope: Partial<Pick<TriggerRecord, 'vehicleIds' | 'vehicleGroupIds' | 'geofenceIds'>> = {}
): Omit<TriggerRecord, 'conditionId'> => ({
  name: formValues.name,
  daysOfWeek: EVERY_DAY,
  startTime: FULL_DAY_START,
  endTime: FULL_DAY_END,
  allowNotifications: formValues.allowNotifications,
  notifiedUsersChannels: formValues.allowNotifications ? formValues.notifiedUsersChannels : [],
  vehicleIds: scope.vehicleIds ?? [],
  vehicleGroupIds: scope.vehicleGroupIds ?? [],
  geofenceIds: scope.geofenceIds ?? [],
});

const copyFor = (type: AlertHookType): AlertHookCopy => ({
  suggestion: {
    iconName: 'siren',
    iconBg: 'bg-primary-3',
    titleKey: `alertHooks:${type}.suggestion.title`,
    descriptionKey: `alertHooks:${type}.suggestion.description`,
    benefitsKey: `alertHooks:${type}.suggestion.benefits`,
  },
  confirm: {
    titleKey: `alertHooks:${type}.confirm.title`,
  },
  defaultNameKey: `alertHooks:${type}.defaultName`,
});

const nameRow = (defaultNameKey: string): AlertHookSummaryField => ({
  labelKey: 'alertHooks:common.summary.nameLabel',
  valueKey: defaultNameKey,
});

const typedRow = (type: AlertHookType, field: 'event' | 'condition'): AlertHookSummaryField => ({
  labelKey: `alertHooks:common.summary.${field}Label`,
  valueKey: `alertHooks:${type}.summary.${field}`,
});

// "Todos os veículos" / "{count} veículo(s) selecionado(s)" via ICU plural. For GEOFENCE_INOUT the
// Figma still shows "Veículos: Todos veículos" — the trigger applies to all fleet vehicles entering
// or leaving the just-created place, since `vehicleIds: []` means fleet-wide.
const vehicleScopeRow = (vehicleIds?: UUID[]): AlertHookSummaryField => ({
  labelKey: 'alertHooks:common.summary.vehiclesLabel',
  valueKey: 'alertHooks:common.summary.vehiclesValue',
  valueParams: { count: vehicleIds?.length ?? 0 },
});

const groupScopeRow = (vehicleGroupIds: UUID[]): AlertHookSummaryField => ({
  labelKey: 'alertHooks:common.summary.groupsLabel',
  valueKey: 'alertHooks:common.summary.groupsValue',
  valueParams: { count: vehicleGroupIds.length },
});

const daysFullRow = (): AlertHookSummaryField => ({
  labelKey: 'alertHooks:common.summary.daysLabel',
  valueKey: 'alertHooks:common.summary.daysValueEveryDay',
});

const timeFullDayRow = (): AlertHookSummaryField => ({
  labelKey: 'alertHooks:common.summary.timeLabel',
  valueKey: 'alertHooks:common.summary.timeValueFullDay',
});

const notificationsAllRow = (): AlertHookSummaryField => ({
  labelKey: 'alertHooks:common.summary.notificationsLabel',
  valueKey: 'alertHooks:common.summary.notificationsValueAll',
});

const typedValueRow = (type: AlertHookType, field: string, labelKey: string): AlertHookSummaryField => ({
  labelKey: `alertHooks:common.summary.${labelKey}`,
  valueKey: `alertHooks:${type}.summary.${field}`,
});

const alertHookConfig: { [K in TriggerAlertHookType]: TriggerAlertHookConfigEntry } & { [K in BlueprintAlertHookType]: BlueprintAlertHookConfigEntry } = {
  [AlertHookType.SPEED]: {
    kind: 'trigger',
    ruleCategory: RuleCategory.VEHICLE_SPEED_LIMIT,
    conditionCategoryDefault: ConditionCategory.VEHICLE_SPEED_LIMIT_1_MINUTE,
    ruleMode: RuleMode.FLEET_POLICY,
    buildFilter: fleetWideActiveFilter,
    getDismissKey: (params) => dismissKeyFor(AlertHookType.SPEED, (params as AlertHookParams<AlertHookType.SPEED>).vehicleIds),
    buildPayload: (params, formValues) => buildBasePayload(formValues, { vehicleIds: (params as AlertHookParams<AlertHookType.SPEED>).vehicleIds }),
    buildSummaryRows: (params) => [
      nameRow(`alertHooks:${AlertHookType.SPEED}.defaultName`),
      typedRow(AlertHookType.SPEED, 'event'),
      typedRow(AlertHookType.SPEED, 'condition'),
      vehicleScopeRow((params as AlertHookParams<AlertHookType.SPEED>).vehicleIds),
      daysFullRow(),
      timeFullDayRow(),
      notificationsAllRow(),
    ],
    copy: copyFor(AlertHookType.SPEED),
  },
  [AlertHookType.IDENTIFICATION]: {
    kind: 'trigger',
    ruleCategory: RuleCategory.DRIVER_IDENTIFICATION,
    conditionCategoryDefault: ConditionCategory.DRIVER_IDENTIFICATION_WITHOUT_DRIVER,
    ruleMode: RuleMode.FLEET_POLICY,
    buildFilter: fleetWideActiveFilter,
    getDismissKey: (params) => {
      const { vehicleIds, vehicleGroupIds } = params as AlertHookParams<AlertHookType.IDENTIFICATION>;
      return dismissKeyFor(AlertHookType.IDENTIFICATION, [...(vehicleIds ?? []), ...(vehicleGroupIds ?? [])]);
    },
    buildPayload: (params, formValues) => {
      const { vehicleIds, vehicleGroupIds } = params as AlertHookParams<AlertHookType.IDENTIFICATION>;
      return {
        ...buildBasePayload(formValues, { vehicleIds, vehicleGroupIds }),
        driverIdentificationWithoutDriverRecurrence: formValues.driverIdentificationWithoutDriverRecurrence ?? DEFAULT_IDENTIFICATION_RECURRENCE,
      };
    },
    buildSummaryRows: (params) => {
      const { vehicleIds, vehicleGroupIds } = params as AlertHookParams<AlertHookType.IDENTIFICATION>;
      const hasGroups = (vehicleGroupIds?.length ?? 0) > 0;
      return [
        nameRow(`alertHooks:${AlertHookType.IDENTIFICATION}.defaultName`),
        typedRow(AlertHookType.IDENTIFICATION, 'condition'),
        // When the journey scoped the trigger only by groups, "Todos os veículos" would misrepresent it.
        ...(!hasGroups || (vehicleIds?.length ?? 0) > 0 ? [vehicleScopeRow(vehicleIds)] : []),
        ...(hasGroups ? [groupScopeRow(vehicleGroupIds ?? [])] : []),
        notificationsAllRow(),
      ];
    },
    copy: copyFor(AlertHookType.IDENTIFICATION),
  },
  [AlertHookType.CAMERA_OBSTRUCTED]: {
    kind: 'trigger',
    ruleCategory: RuleCategory.CAMERAS_HEALTH,
    conditionCategoryDefault: ConditionCategory.DRIVER_CAMERA_OBSTRUCTED,
    ruleMode: RuleMode.FLEET_POLICY,
    buildFilter: fleetWideActiveFilter,
    getDismissKey: (params) => dismissKeyFor(AlertHookType.CAMERA_OBSTRUCTED, (params as AlertHookParams<AlertHookType.CAMERA_OBSTRUCTED>).vehicleIds),
    buildPayload: (params, formValues) => buildBasePayload(formValues, { vehicleIds: (params as AlertHookParams<AlertHookType.CAMERA_OBSTRUCTED>).vehicleIds }),
    buildSummaryRows: (params) => [
      nameRow(`alertHooks:${AlertHookType.CAMERA_OBSTRUCTED}.defaultName`),
      typedRow(AlertHookType.CAMERA_OBSTRUCTED, 'condition'),
      vehicleScopeRow((params as AlertHookParams<AlertHookType.CAMERA_OBSTRUCTED>).vehicleIds),
      notificationsAllRow(),
    ],
    copy: copyFor(AlertHookType.CAMERA_OBSTRUCTED),
  },
  [AlertHookType.CHECKLIST]: {
    kind: 'trigger',
    ruleCategory: RuleCategory.CHECKLIST,
    conditionCategoryDefault: ConditionCategory.CHECKLIST_UNFILLED,
    ruleMode: RuleMode.FLEET_POLICY,
    buildFilter: fleetWideActiveFilter,
    getDismissKey: () => dismissKeyFor(AlertHookType.CHECKLIST),
    buildPayload: (_params, formValues) => buildBasePayload(formValues),
    buildSummaryRows: () => [nameRow(`alertHooks:${AlertHookType.CHECKLIST}.defaultName`), typedRow(AlertHookType.CHECKLIST, 'condition'), notificationsAllRow()],
    copy: copyFor(AlertHookType.CHECKLIST),
  },
  [AlertHookType.GEOFENCE_INOUT]: {
    kind: 'trigger',
    ruleCategory: RuleCategory.GEOFENCE,
    conditionCategoryDefault: ConditionCategory.GEOFENCE_IN_OUT,
    ruleMode: RuleMode.FLEET_POLICY,
    buildFilter: fleetWideActiveFilter,
    getDismissKey: (params) => dismissKeyFor(AlertHookType.GEOFENCE_INOUT, (params as AlertHookParams<AlertHookType.GEOFENCE_INOUT>).geofenceIds),
    buildPayload: (params, formValues) => buildBasePayload(formValues, { geofenceIds: (params as AlertHookParams<AlertHookType.GEOFENCE_INOUT>).geofenceIds }),
    // Figma reference for this type shows: Nome, Condição, Veículos, Dias, Horário, Notificações.
    // The geofence itself is implicit (the just-created place); `vehicleIds: []` means all-fleet.
    buildSummaryRows: () => [
      nameRow(`alertHooks:${AlertHookType.GEOFENCE_INOUT}.defaultName`),
      typedRow(AlertHookType.GEOFENCE_INOUT, 'condition'),
      vehicleScopeRow([]),
      daysFullRow(),
      timeFullDayRow(),
      notificationsAllRow(),
    ],
    copy: copyFor(AlertHookType.GEOFENCE_INOUT),
  },
  [AlertHookType.IDLE_ENGINE]: {
    kind: 'trigger',
    ruleCategory: RuleCategory.IDLE_ENGINE,
    conditionCategoryDefault: ConditionCategory.IDLE_ENGINE_10_MINUTES,
    ruleMode: RuleMode.FLEET_POLICY,
    buildFilter: fleetWideActiveFilter,
    getDismissKey: (params) => dismissKeyFor(AlertHookType.IDLE_ENGINE, (params as AlertHookParams<AlertHookType.IDLE_ENGINE>).vehicleIds),
    buildPayload: (params, formValues) => buildBasePayload(formValues, { vehicleIds: (params as AlertHookParams<AlertHookType.IDLE_ENGINE>).vehicleIds }),
    buildSummaryRows: (params) => [
      nameRow(`alertHooks:${AlertHookType.IDLE_ENGINE}.defaultName`),
      typedRow(AlertHookType.IDLE_ENGINE, 'condition'),
      vehicleScopeRow((params as AlertHookParams<AlertHookType.IDLE_ENGINE>).vehicleIds),
      notificationsAllRow(),
    ],
    copy: copyFor(AlertHookType.IDLE_ENGINE),
  },
  [AlertHookType.CHECKLIST_NON_COMPLIANCE]: {
    kind: 'blueprint',
    alertType: 'checklist-non-compliance',
    eventNameIdentifier: 'CHECKLIST_NON_COMPLIANCE',
    getDismissKey: () => dismissKeyFor(AlertHookType.CHECKLIST_NON_COMPLIANCE),
    // Mirrors the blueprint-api template (AE-2890); the server applies it, this is only the preview.
    buildSummaryRows: () => [
      nameRow(`alertHooks:${AlertHookType.CHECKLIST_NON_COMPLIANCE}.defaultName`),
      typedRow(AlertHookType.CHECKLIST_NON_COMPLIANCE, 'condition'),
      vehicleScopeRow([]),
      typedValueRow(AlertHookType.CHECKLIST_NON_COMPLIANCE, 'frequency', 'frequencyLabel'),
      typedValueRow(AlertHookType.CHECKLIST_NON_COMPLIANCE, 'days', 'daysLabel'),
      typedValueRow(AlertHookType.CHECKLIST_NON_COMPLIANCE, 'time', 'timeLabel'),
      typedValueRow(AlertHookType.CHECKLIST_NON_COMPLIANCE, 'notifications', 'notificationsLabel'),
    ],
    copy: {
      ...copyFor(AlertHookType.CHECKLIST_NON_COMPLIANCE),
      confirm: {
        titleKey: `alertHooks:${AlertHookType.CHECKLIST_NON_COMPLIANCE}.confirm.title`,
        iconName: 'clipboard-check',
        descriptionKey: `alertHooks:${AlertHookType.CHECKLIST_NON_COMPLIANCE}.confirm.description`,
        editCtaKey: `alertHooks:${AlertHookType.CHECKLIST_NON_COMPLIANCE}.confirm.editCta`,
      },
    },
  },
};

/** Whether the fleet already has a blueprint rule created for this event, active or not. */
const hasRuleOfEvent = (triggers: Pick<Trigger, 'event'>[], eventNameIdentifier: string) => triggers.some((trigger) => trigger.event?.nameIdentifier === eventNameIdentifier);

const isTriggerAlertHookType = (type: AlertHookType): type is TriggerAlertHookType => alertHookConfig[type].kind === 'trigger';

/**
 * Resolves the `conditionId` (UUID) the create payload needs, by finding the rule of the hook's
 * `ruleCategory` and the condition matching `conditionCategory` within it. Returns `undefined` when
 * the rule/condition isn't available for the fleet.
 */
const resolveConditionId = (rules: Rule[] | undefined, type: AlertHookType, conditionCategory: ConditionCategory): UUID | undefined => {
  if (!isTriggerAlertHookType(type)) return undefined;
  const rule = rules?.find((candidate) => candidate.category === alertHookConfig[type].ruleCategory);
  return rule?.conditions.find((condition) => condition.category === conditionCategory)?.id;
};

/**
 * Maps the `templateRule`/`templateCondition` query params (the hook's "Editar antes de ativar"
 * link) back to the hook type, so the TriggerPage prefill can reuse this config — same source of
 * truth as the modal preview, only variable content (entity ids) travels in the URL.
 */
const findAlertHookTypeByTemplate = (templateRule: string, templateCondition: string): TriggerAlertHookType | undefined =>
  (Object.keys(alertHookConfig) as AlertHookType[])
    .filter(isTriggerAlertHookType)
    .find((type) => alertHookConfig[type].ruleCategory === templateRule && alertHookConfig[type].conditionCategoryDefault === templateCondition);

export type {
  AlertHookConfigEntry,
  AlertHookCopy,
  AlertHookSummaryField,
  BlueprintAlertHookConfigEntry,
  BlueprintAlertHookType,
  TriggerAlertHookConfigEntry,
  TriggerAlertHookType,
};
export { alertHookConfig, DEFAULT_IDENTIFICATION_RECURRENCE, findAlertHookTypeByTemplate, hasRuleOfEvent, isTriggerAlertHookType, resolveConditionId };
